import { faker } from '@faker-js/faker';

import type { BadgeStatus } from '@/components/ui/badge';

/** The Resend-style event types a webhook can subscribe to. */
export const WEBHOOK_EVENT_TYPES = [
	'email.sent',
	'email.delivered',
	'email.delivery_delayed',
	'email.bounced',
	'email.complained',
	'email.opened',
	'email.clicked',
	'domain.created',
	'domain.deleted',
	'contact.created',
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

/**
 * Webhook delivery outcome. The values are drawn directly from the Badge
 * `status` variant vocabulary so a row/detail component can pass it straight to
 * `<Badge status={event.status} />` with no domain-enum→variant mapping.
 */
export type WebhookEventStatus = Extract<BadgeStatus, 'success' | 'danger'>;

export const WEBHOOK_EVENT_STATUSES: readonly WebhookEventStatus[] = [
	'success',
	'danger',
];

/** One webhook endpoint configuration record. */
export interface WebhookConfig {
	/** Stable id — the index page routes to `/webhooks/:id` on this value. */
	id: string;
	url: string;
	/** Event types this endpoint is subscribed to. */
	listeningFor: WebhookEventType[];
	status: 'enabled' | 'disabled';
	/** ISO-8601 timestamp the endpoint was created. */
	createdAt: string;
	/** Masked signing secret (Resend `whsec_`-style, most chars hidden). */
	signingSecret: string;
}

/** One page of webhook configs, cursor-paginated for `useInfiniteQuery`. */
export interface WebhookConfigPage {
	configs: WebhookConfig[];
	/** Pass as the next `cursor`; `null` when the pool is exhausted. */
	nextCursor: string | null;
}

/** One delivered (or failed) webhook event in the endpoint's activity feed. */
export interface WebhookEvent {
	id: string;
	type: WebhookEventType;
	/** ISO-8601 timestamp of the (most recent) delivery attempt. */
	timestamp: string;
	status: WebhookEventStatus;
	httpStatusCode: number;
	attempts: number;
	/** Short JSON string — the endpoint's response body. */
	responseBody: string;
	/** Larger JSON string — the event payload we POSTed (plausibly truncatable). */
	messagePayload: string;
}

/** One page of webhook events, cursor-paginated for `useInfiniteQuery`. */
export interface WebhookEventPage {
	events: WebhookEvent[];
	/** Pass as the next `cursor`; `null` when the feed is exhausted. */
	nextCursor: string | null;
}

const EVENT_POOL_SIZE = 72;
const CONFIG_POOL_SIZE = 3;
const DEFAULT_PAGE_SIZE = 20;

function buildSigningSecret(): string {
	return `whsec_${'•'.repeat(24)}${faker.string.alphanumeric(4)}`;
}

function buildConfig(): WebhookConfig {
	// A varied subset of event types per endpoint (>=1) so distinct configs read
	// as genuinely different subscriptions in the index list.
	const listeningFor = faker.helpers.arrayElements(
		WEBHOOK_EVENT_TYPES,
		{ min: 2, max: WEBHOOK_EVENT_TYPES.length },
	) as WebhookEventType[];

	return {
		id: faker.string.uuid(),
		url: `https://${faker.internet.domainName()}/api/webhooks/resend`,
		listeningFor,
		status: faker.helpers.weightedArrayElement([
			{ weight: 75, value: 'enabled' as const },
			{ weight: 25, value: 'disabled' as const },
		]),
		createdAt: faker.date.past({ years: 1 }).toISOString(),
		signingSecret: buildSigningSecret(),
	};
}

function buildPayload(type: WebhookEventType): string {
	return JSON.stringify(
		{
			type,
			created_at: faker.date.recent({ days: 7 }).toISOString(),
			data: {
				email_id: faker.string.uuid(),
				from: faker.internet.email().toLowerCase(),
				to: [faker.internet.email().toLowerCase()],
				subject: faker.lorem.sentence(),
				tags: { environment: faker.helpers.arrayElement(['prod', 'staging']) },
			},
		},
		null,
		2,
	);
}

function buildEvent(listeningFor: readonly WebhookEventType[]): WebhookEvent {
	const type = faker.helpers.arrayElement(listeningFor);
	const status = faker.helpers.weightedArrayElement<WebhookEventStatus>([
		{ weight: 85, value: 'success' },
		{ weight: 15, value: 'danger' },
	]);
	const httpStatusCode =
		status === 'success'
			? 200
			: faker.helpers.arrayElement([408, 429, 500, 502, 503]);

	return {
		id: faker.string.uuid(),
		type,
		timestamp: faker.date.recent({ days: 14 }).toISOString(),
		status,
		httpStatusCode,
		attempts: status === 'success' ? 1 : faker.number.int({ min: 1, max: 5 }),
		responseBody:
			status === 'success'
				? '{"ok":true}'
				: JSON.stringify({ error: faker.hacker.phrase() }),
		messagePayload: buildPayload(type),
	};
}

// --- Persistent in-memory store -------------------------------------------
// A module-level singleton so a replay mutation is observable on later reads
// (the SAME event id reflects the update). Generated lazily on first access;
// order is fixed at generation time (never re-sorted) so cursor-by-id paging
// stays stable even after a replay rewrites an event's timestamp.

let configStore: WebhookConfig[] | null = null;
let eventStore: WebhookEvent[] | null = null;

function getConfigStore(): WebhookConfig[] {
	configStore ??= Array.from({ length: CONFIG_POOL_SIZE }, () => buildConfig());
	return configStore;
}

function getEventStore(): WebhookEvent[] {
	if (!eventStore) {
		// The activity feed belongs to the primary (first) endpoint.
		const { listeningFor } = getConfigStore()[0];
		eventStore = Array.from({ length: EVENT_POOL_SIZE }, () =>
			buildEvent(listeningFor),
		).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
	}
	return eventStore;
}

/**
 * The primary endpoint config (first in the pool). Kept for the single-config
 * webhooks page until the index/detail restructure routes off the pool.
 */
export function getWebhookConfigMock(): WebhookConfig {
	return getConfigStore()[0];
}

/** One cursor-paginated page of endpoint configs (mirrors the events feed). */
export function getWebhookConfigsMock(params?: {
	cursor?: string | null;
	limit?: number;
}): WebhookConfigPage {
	const { cursor = null, limit = DEFAULT_PAGE_SIZE } = params ?? {};
	const store = getConfigStore();

	const start = cursor ? store.findIndex((c) => c.id === cursor) + 1 : 0;
	const configs = store.slice(start, start + limit);
	const nextCursor =
		start + limit < store.length && configs.length > 0
			? configs[configs.length - 1].id
			: null;

	return { configs, nextCursor };
}

/** Look up a single config by id (index -> detail routing helper). */
export function getWebhookConfigByIdMock(id: string): WebhookConfig | undefined {
	return getConfigStore().find((c) => c.id === id);
}

/** One cursor-paginated page of events, newest first. */
export function getWebhookEventsMock(params?: {
	cursor?: string | null;
	limit?: number;
}): WebhookEventPage {
	const { cursor = null, limit = DEFAULT_PAGE_SIZE } = params ?? {};
	const store = getEventStore();

	const start = cursor
		? store.findIndex((e) => e.id === cursor) + 1
		: 0;
	const events = store.slice(start, start + limit);
	const nextCursor =
		start + limit < store.length && events.length > 0
			? events[events.length - 1].id
			: null;

	return { events, nextCursor };
}

/** Look up a single event by id (test/detail helper). */
export function getWebhookEventByIdMock(id: string): WebhookEvent | undefined {
	return getEventStore().find((e) => e.id === id);
}

/**
 * Re-deliver an event: increments `attempts`, stamps a fresh `timestamp`, and
 * rolls a fresh outcome. Replaces the persistent store slot with a NEW event
 * object (rather than mutating in place) so subsequent reads of the SAME id
 * reflect the update AND carry a fresh object reference.
 *
 * The fresh reference matters for the consumer: React Query's structural sharing
 * keys change-detection on reference identity, so mutating in place would return
 * a referentially-identical page and the events list would never re-render (only
 * the detail pane, which re-renders off its own mutation state, would update).
 */
export function replayWebhookEventMock(id: string): WebhookEvent {
	const store = getEventStore();
	const index = store.findIndex((e) => e.id === id);
	if (index === -1) {
		throw new Error(`replayWebhookEventMock: unknown event id ${id}`);
	}

	const prev = store[index];
	const status = faker.helpers.weightedArrayElement<WebhookEventStatus>([
		{ weight: 70, value: 'success' },
		{ weight: 30, value: 'danger' },
	]);

	const updated: WebhookEvent = {
		...prev,
		attempts: prev.attempts + 1,
		timestamp: new Date().toISOString(),
		status,
		httpStatusCode:
			status === 'success'
				? 200
				: faker.helpers.arrayElement([408, 429, 500, 502, 503]),
		responseBody:
			status === 'success'
				? '{"ok":true}'
				: JSON.stringify({ error: faker.hacker.phrase() }),
	};

	store[index] = updated;
	return updated;
}
